import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { getAccuracyTrends } from "../../../../services/Dashboard/Table";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="flex flex-col gap-2.5 bg-[var(--color-bg)] border border-[var(--color-border)] p-4 rounded-[var(--radius-md)] backdrop-blur-md">
        <h4 className="m-0 md:text-base text-sm font-black text-[var(--color-text)] uppercase tracking-wider">
          {data.month}
        </h4>
        <div className="flex flex-col gap-2 md:text-sm text-xs text-[var(--color-text-muted)]">
          <div className="flex justify-between items-center gap-6">
            <span className="flex items-center gap-2 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-primary)]" />
              Win Rate:
            </span>
            <span className="font-black text-[var(--color-primary)] tabular">{data.winRate}%</span>
          </div>
          <div className="flex justify-between items-center gap-6">
            <span className="flex items-center gap-2 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-[#38BDF8]" />
              Place Rate:
            </span>
            <span className="font-black text-[#38BDF8] tabular">{data.placeRate}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

function PredictionAnalyticsModule() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    let ignore = false;
    const loadAccuracyTrends = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getAccuracyTrends();
        if (!ignore) {
          const parsed = (response.results || []).map((item) => {
            const parts = (item.period_label || "").split(" ");
            const monthName = parts[0] || "";
            const shortLabel = monthName.substring(0, 3);
            return {
              month: item.period_label,
              shortMonth: shortLabel,
              winRate: parseFloat(item.win_rate_pct) || 0,
              placeRate: parseFloat(item.place_rate_pct) || 0,
            };
          });
          // Show max 12 months (keep only the most recent 12)
          setData(parsed.slice(-12));
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || err?.detail || err?.error || "Failed to load accuracy trends.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadAccuracyTrends();
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <div className="grid gap-4">
      {/* Header Info */}
      <div className="flex flex-col gap-2 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
        <h4 className="m-0 xl:text-xl md:text-lg text-base font-bold text-[var(--color-text)]">
          Model Accuracy & Performance Trends
        </h4>
        <p className="m-0 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] leading-[1.6]">
          Visualizing the historic win and place prediction accuracies computed by the AI The FormGuru engine over time. Hover over data points to review specific details.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] md:text-sm text-xs">
          {error}
        </div>
      )}

      {/* Chart Card */}
      <div className="p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-col gap-1">
            <span className="md:text-sm text-xs label-caps block m-0">Accuracy Trend</span>
            <h3 className="m-0 xl:text-xl md:text-lg text-base font-black text-[var(--color-text)]">Historical Strikerates</h3>
          </div>
          {/* Custom Legend */}
          <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <span className="w-3 h-1 bg-[var(--color-primary)] rounded-full" />
              <span className="text-[var(--color-primary)]">Win Rate</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-1 bg-[#38BDF8] rounded-full" />
              <span className="text-[#38BDF8]">Place Rate</span>
            </div>
          </div>
        </div>

        {/* Recharts Container */}
        <div className="w-full h-[380px] relative">
          {loading ? (
            <div className="w-full h-full flex flex-col justify-between animate-pulse">
              <div className="flex flex-col flex-1 justify-between">
                {[100, 75, 50, 25, 0].map((val) => (
                  <div key={val} className="flex items-center gap-4 w-full">
                    <span className="w-8 text-right text-xs font-bold text-[var(--color-text-muted)]">{val}%</span>
                    <div className="flex-1 border-t border-dashed border-[var(--color-text-muted)]" />
                  </div>
                ))}
              </div>
              <div className="flex justify-between pl-14 pr-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-8 h-3 bg-[var(--color-text-muted)] rounded-[var(--radius-md)] animate-pulse" />
                ))}
              </div>
            </div>
          ) : data.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="xl:text-xl md:text-lg text-base font-bold text-[var(--color-text-muted)]">
                No accuracy trends data found.
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="winRateGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="placeRateGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#38BDF8" stopOpacity={0.0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  stroke="var(--color-text-light)"
                  strokeDasharray="4 4"
                  vertical={false}
                />
                
                <XAxis
                  dataKey="month"
                  stroke="var(--color-text-muted)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                  tickFormatter={(value) => {
                    const parts = (value || "").split(" ");
                    const monthName = parts[0] || "";
                    if (!monthName) return "";
                    return monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase();
                  }}
                />
                
                <YAxis
                  width={45}
                  stroke="var(--color-text-muted)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  dx={-5}
                />
                
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: "var(--color-text-light)", strokeWidth: 1.5, strokeDasharray: "3 3" }}
                />

                <Area
                  type="monotone"
                  dataKey="winRate"
                  name="Win Rate"
                  stroke="var(--color-primary)"
                  strokeWidth={3}
                  fill="url(#winRateGlow)"
                  dot={{ r: 4, stroke: "var(--color-bg)", strokeWidth: 2, fill: "var(--color-primary)" }}
                  activeDot={{ r: 6, stroke: "var(--color-primary)", strokeWidth: 2, fill: "#000" }}
                  animationDuration={1500}
                />
                
                <Area
                  type="monotone"
                  dataKey="placeRate"
                  name="Place Rate"
                  stroke="#38BDF8"
                  strokeWidth={3}
                  fill="url(#placeRateGlow)"
                  dot={{ r: 4, stroke: "var(--color-bg)", strokeWidth: 2, fill: "#38BDF8" }}
                  activeDot={{ r: 6, stroke: "#38BDF8", strokeWidth: 2, fill: "#000" }}
                  animationDuration={1500}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default PredictionAnalyticsModule;
